import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const tick = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (!active) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState("pending");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (!active) return;
        setState("error");
      }
    };
    tick();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-6 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-steel-brass" />
          <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Confirming your payment…</h1>
          <p className="mt-3 text-sm text-steel-muted">One moment while we verify with our payment processor.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-400" data-testid="payment-paid-icon" />
          <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Payment received</h1>
          <p className="mt-3 text-sm text-steel-secondary">
            Thank you{info?.invoice_number ? ` — invoice ${info.invoice_number} is now marked paid` : ""}.
            A receipt has been sent to your email.
          </p>
          <button
            data-testid="payment-back-to-portal"
            onClick={() => navigate("/portal")}
            className="mt-8 flex items-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20"
          >
            Back to your portal <ArrowRight className="h-4 w-4" />
          </button>
        </>
      )}
      {state === "pending" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-steel-brass" />
          <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Still processing</h1>
          <p className="mt-3 text-sm text-steel-secondary">
            Your payment is being finalized. It may take a moment to reflect — check your portal shortly.
          </p>
          <button
            data-testid="payment-back-to-portal"
            onClick={() => navigate("/portal")}
            className="mt-8 flex items-center gap-2 border border-steel-border px-6 py-3 text-sm text-steel-secondary transition-colors hover:text-steel-text"
          >
            Back to your portal <ArrowRight className="h-4 w-4" />
          </button>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="h-14 w-14 text-red-400" />
          <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">We couldn't confirm payment</h1>
          <p className="mt-3 text-sm text-steel-secondary">
            If you were charged, it will still reflect in your portal shortly. Otherwise, please try again.
          </p>
          <button
            onClick={() => navigate("/portal")}
            className="mt-8 flex items-center gap-2 border border-steel-border px-6 py-3 text-sm text-steel-secondary transition-colors hover:text-steel-text"
          >
            Back to your portal <ArrowRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-6 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle className="h-14 w-14 text-steel-muted" />
      <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Payment cancelled</h1>
      <p className="mt-3 text-sm text-steel-secondary">
        No charge was made. Your invoice is still waiting whenever you're ready.
      </p>
      <button
        data-testid="payment-cancel-back"
        onClick={() => navigate("/portal")}
        className="mt-8 flex items-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20"
      >
        Back to your portal <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
